import { Shield } from 'lucide-react';
import { useSystemHealth, type HealthStatus } from '@/hooks/useSystemHealth';

const statusConfig: Record<HealthStatus, { bg: string; text: string; dot: string }> = {
  healthy: {
    bg: 'border-wt-green-primary/20',
    text: 'text-wt-green-primary',
    dot: 'bg-wt-green-primary',
  },
  attention: {
    bg: 'border-wt-amber-primary/20',
    text: 'text-wt-amber-primary',
    dot: 'bg-wt-amber-primary',
  },
  issue: {
    bg: 'border-wt-red-primary/20',
    text: 'text-wt-red-primary',
    dot: 'bg-wt-red-primary',
  },
};

export function SystemHealthCard({ propertyId }: { propertyId: string }) {
  const { data, isLoading } = useSystemHealth(propertyId);

  if (isLoading || !data) {
    return (
      <div className="flex-1 rounded-lg border border-border bg-card p-6 space-y-2 animate-pulse">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">System Health</span>
          <Shield className="w-5 h-5 text-muted-foreground" />
        </div>
        <div className="h-8 w-24 bg-muted rounded" />
        <div className="h-4 w-48 bg-muted rounded" />
      </div>
    );
  }

  const config = statusConfig[data.status];

  return (
    <div className={`flex-1 rounded-lg border bg-card p-6 space-y-2 ${config.bg}`}>
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">System Health</span>
        <Shield className={`w-5 h-5 ${config.text}`} />
      </div>
      <div className="flex items-center gap-2">
        <span className={`inline-block w-3 h-3 rounded-full ${config.dot}`} />
        <p className={`font-mono text-[28px] font-semibold leading-tight tracking-tight ${config.text}`}>
          {data.label}
        </p>
      </div>
      <p className="text-xs text-muted-foreground">{data.explanation}</p>
    </div>
  );
}
